import { useCallback, useEffect } from 'react'
import { AppState, AppStateStatus, Platform } from 'react-native'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { useGameState } from '../useGameState'
import { useGameContext } from '../GameContext'

function isMobileUserAgent() {
  const regexp = new RegExp(
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|bolt|bos|bounce|maemo|midp|mmp|mobile.+firefox|opera.+mobi|palm|phone|pie|pli|xiino|mobi|lynx|mowser|[^ai]ppc;|iemobile|htc|java.+mobile|fone|[^ai]ppc;|tablet|kindle/i
  )
  return regexp.test(navigator.userAgent)
}
/**
 * Hook to automatically pause the game when the app goes to background or becomes inactive.
 */
export function useAutoPauseOnBackground() {
  const { isPlaying, isPaused, game } = useGameState()
  const { gameId } = useGameContext()
  const pauseGame = useMutation(api.games.mutations.pauseGame)

  const handleAppStateChange = useCallback(
    async (nextAppState: AppStateStatus) => {
      const goingToBackground = nextAppState === 'background' || nextAppState === 'inactive'

      if (!goingToBackground) return

      // Only pause solo mode games (practice, daily10)
      const isSoloMode = game?.mode === 'practice' || game?.mode === 'daily10'
      if (!isSoloMode) return

      // Only pause if game is playing, not already paused, and has a valid id
      if (!(gameId && isPlaying) || isPaused) return
      if (Platform.OS === 'web' && !isMobileUserAgent()) {
        return
      }
      try {
        await pauseGame({ gameId })
      } catch (error) {
        console.error('[useAutoPauseOnBackground] Auto-pause on background/inactive failed', error)
      }
    },
    [isPlaying, isPaused, gameId, pauseGame, game]
  )

  useEffect(() => {
    const subscription = AppState.addEventListener('change', handleAppStateChange)
    return () => subscription.remove()
  }, [handleAppStateChange])
}
